/**
 * How the trigger is chromed, expressed as design-system `SelectTrigger` variants.
 *
 * The `button` variant is borderless and transparent; `default` draws a real form control, which
 * also picks up the focus ring when the menu opens (`SelectTrigger` handles that via
 * `data-[state=open]`).
 */
type TriggerAppearanceSpec = {
  /** Design-system `SelectTrigger` variant: 'button' is borderless, 'default' draws a border. */
  variant: 'default' | 'button'
  inputSize: 'sm' | 'large'
  /** Width of the dropdown panel. */
  contentClassName: string
}

/**
 * Inline menus float free of their trigger, so they need their own minimum width. A bordered
 * trigger is a sized form control, so its panel lines up with the trigger instead.
 */
const INLINE_MENU_WIDTH = 'min-w-72'
const MATCH_TRIGGER_WIDTH = 'min-w-[var(--radix-select-trigger-width)]'

export type TriggerAppearance = 'headless' | 'default'

export const triggerAppearanceProps: Record<TriggerAppearance, TriggerAppearanceSpec> = {
  /** Bare content, no chrome — badges inside table cells and inline menus. */
  headless: { variant: 'button', inputSize: 'sm', contentClassName: INLINE_MENU_WIDTH },
  /** A standalone bordered form control. */
  default: { variant: 'default', inputSize: 'large', contentClassName: MATCH_TRIGGER_WIDTH },
}

const YEAR_DROPDOWN_OVERLAP_OFFSET_PX = -215

export const selectDropdownPositioningVariants = {
  default: {
    align: undefined,
    className: undefined,
    sideOffset: undefined,
    viewportClassName: undefined,
    centerSelectedItem: false,
    useViewportScrollContainer: false,
  },
  overlapTrigger: {
    align: 'center' as const,
    className: 'min-w-[calc(var(--radix-select-trigger-width)+0.5rem)]',
    sideOffset: YEAR_DROPDOWN_OVERLAP_OFFSET_PX,
    viewportClassName: 'max-h-[396px]',
    centerSelectedItem: true,
    useViewportScrollContainer: true,
  },
} as const

export type SelectDropdownPositioning = keyof typeof selectDropdownPositioningVariants
